import { useTranslation } from 'react-i18next'
import { MaterialSymbol } from 'react-material-symbols'
import { useLocation, useNavigate } from 'react-router-dom'

import { Menu } from 'primereact/menu'
import { MenuItem, MenuItemOptions } from 'primereact/menuitem'

import { useProjectByTenantIdAndProjectId } from '../../../../../data/tenants'
import {
  Permissions,
  useProjectPermissions,
  useSupplierFeatures,
} from '../../../../../features/authorization'
import { isActiveClassName } from '../../../../../utils/helper/navigation/isActiveClassName'
import { useCurrentTenantConfig } from '../../../../../utils/tenants'
import { MenuItemTemplate } from './MenuItemTemplate'

type Props = {
  handleResize: () => void
  isNavMinimized: boolean
}

export const MicroAppNavigation: React.FC<Props> = ({
  handleResize,
  isNavMinimized,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const currentLocation = location.pathname
  const { t } = useTranslation()

  const { tenantId, projectId } = useCurrentTenantConfig()
  const { data } = useProjectByTenantIdAndProjectId(
    tenantId as string,
    projectId as string,
  )
  const { data: permissions } = useProjectPermissions()
  const supplierFeatures = useSupplierFeatures()

  const buildItems = (): MenuItem[] => {
    const lowerCasePermissions = permissions?.map(p => p.name.toLowerCase())

    let items: MenuItem[] = []

    // Demo Functions
    if (
      lowerCasePermissions?.includes(Permissions.Project.Trial.toLowerCase())
    ) {
      items.push({
        id: 'menu-item-trial',
        label: t('sidebar.seller.labels.trial'),
        icon: <MaterialSymbol icon="note_add" size={24} />,
        command: () => {
          navigate('/demo-functions')
          handleResize()
        },
        className: isActiveClassName(currentLocation, '/demo-functions'),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    // Delivery Forecasts
    if (
      supplierFeatures.isDeliveryForecastActive &&
      lowerCasePermissions?.includes(
        Permissions.Project.DeliveryForecastWorklist.toLowerCase(),
      )
    ) {
      items.push({
        id: 'menu-item-delivery-forecasts',
        label: t('sidebar.seller.labels.worklistDeliveryForecast'),
        icon: <MaterialSymbol icon="calendar_month" size={24} />,
        command: () => {
          navigate('/delivery-forecasts')
          handleResize()
        },
        className:
          isActiveClassName(currentLocation, '/delivery-forecasts', true) ||
          isActiveClassName(
            currentLocation,
            '/delivery-forecasts/delivery-forecast',
          ) ||
          isActiveClassName(
            currentLocation,
            '/delivery-forecasts/despatch-advice',
          ),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    // Delivery Forecasts History
    if (
      supplierFeatures.isDeliveryForecastActive &&
      lowerCasePermissions?.includes(
        Permissions.Project.DeliveryForecastHistory.toLowerCase(),
      )
    ) {
      items.push({
        id: 'menu-item-delivery-forecasts-history',
        label: t('sidebar.seller.labels.historyDeliveryForecasts'),
        icon: <MaterialSymbol icon="event_repeat" size={24} />,
        command: () => {
          navigate('/delivery-forecasts/history')
          handleResize()
        },
        className: isActiveClassName(
          currentLocation,
          '/delivery-forecasts/history',
        ),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    // Orders
    if (
      supplierFeatures.isOrderProcessActive &&
      lowerCasePermissions?.includes(Permissions.Project.Worklist.toLowerCase())
    ) {
      items.push({
        id: 'menu-item-orders',
        label: t('sidebar.seller.labels.worklist'),
        icon: <MaterialSymbol icon="shopping_cart" size={24} />,
        command: () => {
          navigate('/orders')
          handleResize()
        },
        className:
          isActiveClassName(currentLocation, '/') ||
          isActiveClassName(currentLocation, '/orders', true) ||
          isActiveClassName(currentLocation, '/orders/order') ||
          isActiveClassName(currentLocation, '/orders/despatch-advice') ||
          isActiveClassName(currentLocation, '/orders/invoice'),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    // Orders History
    if (
      supplierFeatures.isOrderProcessActive &&
      lowerCasePermissions?.includes(Permissions.Project.History.toLowerCase())
    ) {
      items.push({
        id: 'menu-item-orders-history',
        label: t('sidebar.seller.labels.history'),
        icon: <MaterialSymbol icon="book" size={24} />,
        command: () => {
          navigate('/orders/history')
          handleResize()
        },
        className: isActiveClassName(currentLocation, '/orders/history'),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    // EInvoice
    if (
      supplierFeatures.isEInvoiceActive &&
      lowerCasePermissions?.includes(Permissions.Project.EInvoice.toLowerCase())
    ) {
      items.push({
        id: 'menu-item-invoice',
        label: t('sidebar.seller.labels.eInvoice'),
        icon: <MaterialSymbol icon="request_quote" size={24} />,
        command: () => {
          navigate('/e-invoices')
          handleResize()
        },
        className:
          isActiveClassName(currentLocation, '/e-invoices', true) ||
          isActiveClassName(currentLocation, '/e-invoices/', true) ||
          (currentLocation.startsWith('/e-invoices/') &&
            !currentLocation.includes('/e-invoices/history'))
            ? 'active'
            : '',
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    // EInvoice History
    if (
      supplierFeatures.isEInvoiceActive &&
      lowerCasePermissions?.includes(
        Permissions.Project.EInvoiceHistory.toLowerCase(),
      )
    ) {
      items.push({
        id: 'menu-item-e-invoice-history',
        label: t('sidebar.seller.labels.historyEInvoice'),
        icon: <MaterialSymbol icon="overview" size={24} />,
        command: () => {
          navigate('/e-invoices/history')
          handleResize()
        },
        className: isActiveClassName(currentLocation, '/e-invoices/history'),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    // Members
    if (
      lowerCasePermissions?.includes(
        Permissions.Project.ProjectUserAdmin.toLowerCase(),
      )
    ) {
      items.push({
        id: 'menu-item-members',
        label: t('sidebar.seller.labels.userManagement'),
        icon: <MaterialSymbol icon="group" size={24} />,
        command: () => {
          navigate('/members')
          handleResize()
        },
        className: isActiveClassName(currentLocation, '/members'),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    // Settings
    if (
      lowerCasePermissions?.includes(
        Permissions.Project.ProjectSettings.toLowerCase(),
      )
    ) {
      items.push({
        id: 'menu-item-settings',
        label: t('sidebar.seller.labels.settings'),
        icon: <MaterialSymbol icon="settings" size={24} />,
        command: () => {
          navigate('/settings')
          handleResize()
        },
        className: isActiveClassName(currentLocation, '/settings'),
        template: (item: MenuItem, options: MenuItemOptions) => (
          <MenuItemTemplate
            item={item}
            options={options}
            isNavMinimized={isNavMinimized}
          />
        ),
      })
    }

    return [
      {
        label: t('sidebar.seller.title'),
        items: items,
      },
    ]
  }

  if (
    !permissions ||
    permissions.filter(p => p.name.toLowerCase().startsWith('webedi')).length <
      1
  )
    return null

  return <Menu model={buildItems()} />
}
